import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const SORT_FIELDS = {
  invoice_date: (r) => r.invoice_date || "",
  invoice_number: (r) => r.invoice_number || "",
  descripcion: (r) => (r.descripcion || "").toLowerCase(),
  precio_unitario_sin_iva: (r) =>
    r.precio_unitario_sin_iva != null ? Number(r.precio_unitario_sin_iva) : -Infinity,
  descuento: (r) => Number(r.descuento || 0),
  precio_con_descuento_sin_iva: (r) =>
    r.precio_con_descuento_sin_iva != null ? Number(r.precio_con_descuento_sin_iva) : -Infinity,
  supplier_name: (r) => (r.supplier_name || "").toLowerCase(),
  cantidad: (r) => (r.cantidad != null ? Number(r.cantidad) : -Infinity),
  precio_unitario: (r) => (r.precio_unitario != null ? Number(r.precio_unitario) : -Infinity),
  iva_pct: (r) => (r.iva_pct != null ? Number(r.iva_pct) : -Infinity),
};

function SortHead({ field, children, align = "left", sortBy, sortDir, onToggle }) {
  const active = sortBy === field;
  return (
    <th
      className={`text-${align} p-4 label-cap cursor-pointer select-none whitespace-nowrap`}
      onClick={() => onToggle(field)}
      data-testid={`sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && (sortDir === "asc" ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />)}
      </span>
    </th>
  );
}

export default function AdminItemSearch() {
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [perSupplierLimit, setPerSupplierLimit] = useState("2");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("invoice_date");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { q };
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (!dateFrom && !dateTo) {
          const n = parseInt(perSupplierLimit) || 0;
          if (n > 0) params.per_supplier_limit = n;
        }
        const { data } = await api.get("/items/search", { params });
        setResults(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, dateFrom, dateTo, perSupplierLimit]);

  const sortedResults = useMemo(() => {
    const getter = SORT_FIELDS[sortBy] || SORT_FIELDS.invoice_date;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...results].sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [results, sortBy, sortDir]);

  const rangeActive = !!(dateFrom || dateTo);
  const limitN = Math.max(0, Math.min(50, parseInt(perSupplierLimit) || 0));

  const toggleSort = useCallback((field) => {
    setSortBy((prevBy) => {
      if (prevBy === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prevBy;
      }
      setSortDir(field === "descripcion" || field === "supplier_name" ? "asc" : "desc");
      return field;
    });
  }, []);

  return (
    <div>
      <div className="mb-8">
        <div className="label-cap">búsqueda global</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Buscar artículos</h1>
        <p className="text-[var(--text-secondary)] text-sm mt-1">
          Busca por descripción entre todas las facturas de todos los proveedores.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <div className="relative md:col-span-4">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ej: aceite, jamón, coca-cola…"
            data-testid="item-search-input"
            autoFocus
            className="w-full pl-12 pr-4 py-4 rounded-xl border border-[var(--border)] bg-white focus:outline-none focus:border-[var(--primary)] font-display text-lg"
          />
        </div>
        <div>
          <label className="label-cap block mb-2">Nº de registros</label>
          <input
            type="number"
            min="0"
            max="50"
            value={perSupplierLimit}
            onChange={(e) => setPerSupplierLimit(e.target.value.replace(/\D/g, "").slice(0, 2))}
            data-testid="item-per-supplier-limit"
            disabled={rangeActive}
            placeholder="0 = todos"
            className={`w-full h-11 px-3 rounded-lg border border-[var(--border)] bg-white font-display ${rangeActive ? "opacity-50 cursor-not-allowed" : ""}`}
          />
        </div>
        <div>
          <label className="label-cap block mb-2">Desde</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="item-date-from"
            className="w-full h-11 px-3 rounded-lg border border-[var(--border)] bg-white font-display"
          />
        </div>
        <div>
          <label className="label-cap block mb-2">Hasta</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="item-date-to"
            className="w-full h-11 px-3 rounded-lg border border-[var(--border)] bg-white font-display"
          />
        </div>
        <div>
          <label className="label-cap block mb-2 opacity-0 select-none">—</label>
          {(dateFrom || dateTo) ? (
            <button
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              className="w-full h-11 px-3 rounded-lg border border-[var(--border)] font-display text-sm hover:bg-white"
              data-testid="item-clear-dates"
            >
              Limpiar fechas
            </button>
          ) : (
            <div className="h-11 flex items-center text-[11px] text-[var(--text-secondary)] px-1">
              {`Últimos ${limitN > 0 ? limitN : "todos"} registros por proveedor`}
            </div>
          )}
        </div>
      </div>

      {loading && <div className="text-center py-8 text-[var(--text-secondary)]">Buscando…</div>}

      {!loading && q.length >= 2 && results.length === 0 && (
        <div className="card-flat p-8 text-center text-[var(--text-secondary)]">
          Sin resultados para &quot;{q}&quot;.
        </div>
      )}

      {results.length > 0 && (
        <div className="card-flat overflow-x-auto" data-testid="items-results">
          <div className="p-4 border-b border-[var(--border)] bg-[var(--bg)] text-sm text-[var(--text-secondary)] flex items-center justify-between">
            <span>{results.length} resultado(s)</span>
            <span className="text-xs">
              Pulsa una cabecera para ordenar.
            </span>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <SortHead field="descripcion" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Artículo</SortHead>
                <SortHead field="cantidad" align="right" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Unid.</SortHead>
                <SortHead field="precio_unitario_sin_iva" align="right" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>P/U sin IVA</SortHead>
                <SortHead field="descuento" align="right" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Dto.</SortHead>
                <SortHead field="precio_con_descuento_sin_iva" align="right" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>P/U con Dto.</SortHead>
                <SortHead field="precio_unitario" align="right" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>P/U con IVA</SortHead>
                <SortHead field="iva_pct" align="right" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>IVA</SortHead>
                <SortHead field="supplier_name" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Proveedor</SortHead>
                <SortHead field="invoice_number" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Factura</SortHead>
                <SortHead field="invoice_date" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort}>Fecha</SortHead>
              </tr>
            </thead>
            <tbody>
              {sortedResults.map((r, i) => {
                const hasDto = Number(r.descuento || 0) > 0;
                return (
                  <tr key={`${r.invoice_id}-${i}`} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg)]">
                    <td className="p-4 font-display font-semibold">{r.descripcion}</td>
                    <td className="p-4 text-right font-mono">
                      {r.cantidad != null ? Number(r.cantidad) : "—"}
                    </td>
                    <td className={`p-4 text-right font-mono ${hasDto ? "text-[var(--text-secondary)] line-through" : "font-bold text-[var(--primary)]"}`}>
                      {r.precio_unitario_sin_iva != null ? Number(r.precio_unitario_sin_iva).toFixed(2) + " €" : "—"}
                    </td>
                    <td className={`p-4 text-right font-mono ${hasDto ? "text-emerald-600 font-semibold" : "text-[var(--text-secondary)]"}`}>
                      {hasDto ? `${Number(r.descuento).toFixed(1)}%` : "—"}
                    </td>
                    <td className={`p-4 text-right font-mono ${hasDto ? "font-bold text-[var(--primary)]" : "text-[var(--text-secondary)]"}`}>
                      {r.precio_con_descuento_sin_iva != null ? Number(r.precio_con_descuento_sin_iva).toFixed(2) + " €" : "—"}
                    </td>
                    <td className="p-4 text-right font-mono text-[var(--text-secondary)]">
                      {r.precio_unitario != null ? Number(r.precio_unitario).toFixed(2) + " €" : "—"}
                    </td>
                    <td className="p-4 text-right font-mono text-xs text-[var(--text-secondary)]">
                      {r.iva_pct != null ? `${r.iva_pct}%` : "—"}
                    </td>
                    <td className="p-4">
                      <Link to={`/admin/suppliers/${r.supplier_id}`} className="text-[var(--primary)] hover:underline">
                        {r.supplier_name || "—"}
                      </Link>
                    </td>
                    <td className="p-4 font-mono text-sm text-[var(--text-secondary)]">{r.invoice_number || "—"}</td>
                    <td className="p-4 text-sm text-[var(--text-secondary)]">{r.invoice_date || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
